<template>
  <div
    v-if="visible"
    class="magic-ui-list"
  >
    <div
      class="list-container"
      :style="{
        gridTemplateColumns: `repeat(${isNaN(config.columnNumber) ? 1 : config.columnNumber}, 1fr)`,
        gridRowGap: config.gridRowGap,
        gridColumnGap: config.gridColumnGap,
      }"
    >
      <template v-for="(item, index) in currentPageData" :key="item">
        <list-item
          
          :currentItem="item"
          :index="index"
          :currentItemKey="config.currentItemKey"
          :indexKey="config.indexKey"
          :listNode="node"
        >
          <div
            class="list-item-container _magic-ui-container-only-layout"
            :id="`list-item-container-${index}`"
            :style="{
              height: itemHeightComputed,
            }"
            @click="handleItemClick(item, index)"
          >
            <slot></slot>
            <magic-ui-component v-for="item in childrenConfig" :config="item"></magic-ui-component>
          </div>
        </list-item>
      </template>
    </div>
    <div
      v-if="config.pagination.hideOnSinglePage === false || pagination.total > pagination.pageSize"
      class="pagination-container"
    >
      <el-pagination
        v-model:page-size="pagination.pageSize"
        v-model:current-page="pagination.currentPage"
        :small="true"
        :layout="`prev, pager, next${config.pagination.showQuickJumper ? ', jumper' : ''}${config.pagination.showSizeChanger ? ', sizes' : ''}`"
        :total="pagination.total"
        :page-sizes="config.pagination.pageSizes"
        @current-change="handleCurrentPageChange"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, reactive, ref, watch } from 'vue';
import { cloneDeep } from 'lodash-es';
import { ElPagination } from 'element-plus';
import 'element-plus/es/components/pagination/style/css';
import { MContainer } from '@tmagic/schema';

import useApp from '@/hooks/useApp';
import useDisplay from '@/hooks/useDisplay';

import MagicUiComponent from '../../../Component.vue';

import initValue from '../initValue';

import ListItem from './ListItem.vue';

const props = defineProps<{
  config: MContainer & typeof initValue;
}>();

const app = useApp(props);
const node = app?.page?.getNode(props.config.id!);

const dataSource = computed(() => {
  let data = props.config.dataSource;
  if (Array.isArray(data)) {
    return data;
  } else {
    return [];
  }
});

const currentItem = ref<any>();
const currentIndex = ref<number>();

const resetCurrent = () => {
  currentItem.value = undefined;
  currentIndex.value = undefined;
};

const pagination = ref<any>({
  currentPage: Number(props.config.pagination.currentPage) || 1,
  pageSize: Number(props.config.pagination.pageSize) || 6,
  total: Number(props.config.pagination.total) || 0,
  pageSizes: props.config.pagination.pageSizes || [6, 10, 20, 50, 100],
});

watch(
  () => props.config.pagination,
  (val) => {
    pagination.value.pageSize = Number(val.pageSize) || 6;

    pagination.value.total = Number(val.total) || dataSource.value.length || 0;

    pagination.value.pageSizes = Array.isArray(val.pageSizes) ? val.pageSizes : [6, 10, 20, 50, 100]

    if (val.showSizeChanger) {
      pagination.value.pageSizes.indexOf(pagination.value.pageSize) === -1 && (pagination.value.pageSize = pagination.value.pageSizes[0]);
    }
  },
  { deep: true, immediate: true },
);

const currentPageData = computed(() => {
  if (dataSource.value?.length <= pagination.value.pageSize) {
    return dataSource.value.slice();
  }
  const start = (pagination.value.currentPage - 1) * pagination.value.pageSize;
  const end = start + pagination.value.pageSize;
  return dataSource.value?.slice(start, end) || [];
});

const itemHeightComputed = computed(() => {
  const itemHeight = props.config.itemHeight;
  if (isNaN(Number(itemHeight))) {
    return itemHeight;
  } else {
    return Number(itemHeight) / 100 + 'rem';
  }
});

const childrenConfig = computed(() => {
  return cloneDeep(props.config.items);
});

const handleCurrentPageChange = () => {
  console.log(`组件${props.config.id}的分页切换到了${pagination.value.currentPage}页`);
  app?.emit('pageChange', node);
}

const handleItemClick = (item: any, index: number) => {
  currentItem.value = item;
  currentIndex.value = index;
  console.log(`组件${props.config.id}的单元点击事件触发了`);
  app?.emit('itemClick', node, item, index, event);
}

const setPage = (page: number) => {
  pagination.value.currentPage = page;
}

const deleteItem = (itemIndex: number) => {
  itemIndex = Number(itemIndex)
  if (dataSource.value?.length <= pagination.value.pageSize) {
    dataSource.value.splice(itemIndex, 1);
  } else {
    const start = (pagination.value.currentPage - 1) * pagination.value.pageSize;
    const index = itemIndex + start;
    dataSource.value?.splice(index, 1);
  }
}

const {
  visible,
  show,
  hide,
} = useDisplay(props);

// 暴露给编辑器的接口请注册在这里
const exportInterface = reactive({
  visible,
  show,
  hide,
  dataSource,
  currentItem,
  currentIndex,
  pagination,
  setPage,
  deleteItem,
  currentPageData,
  resetCurrent,
});

defineExpose(exportInterface);

</script>

<style lang="scss" scoped>
.list-container{
  display: grid;
  /* grid-row-gap: 0.15rem;
  grid-column-gap: 0.15rem; */
  .list-item-container{
    position: relative;
  }
}
.pagination-container{
  display: flex;
  justify-content: center;
  padding: 5px 0;
}
</style>
